import Papa from 'papaparse';
import React, { useMemo, useState } from 'react';
import { Modal } from '../../components/ui/modal';
import { api, apiEndpoints } from '../../utils/apiService';
import {
  ProspectPayload,
  isValidEmail,
  normalizeEmail,
  normalizePhone,
} from './types';

const MAX_ROWS = 2000;

type Step = 'upload' | 'map' | 'validate' | 'report';

const TARGET_FIELDS: Array<{
  key: keyof ProspectPayload;
  label: string;
  required?: boolean;
  aliases: string[];
}> = [
  {
    key: 'name',
    label: 'Restaurant name',
    required: true,
    aliases: [
      'name',
      'restaurant_name',
      'company_name',
      'restaurant',
      'business_name',
    ],
  },
  {
    key: 'restaurant_group_name',
    label: 'Restaurant group name',
    aliases: [
      'restaurant_group_name',
      'restaurant_group',
      'group_name',
      'group',
      'parent_company',
      'ownership_group',
    ],
  },
  {
    key: 'phone',
    label: 'Phone',
    aliases: ['phone', 'phone_number', 'telephone'],
  },
  { key: 'website', label: 'Website', aliases: ['website', 'url', 'site'] },
  {
    key: 'google_place_id',
    label: 'Google Place ID',
    aliases: ['google_place_id', 'place_id'],
  },
  {
    key: 'google_maps_url',
    label: 'Google Maps URL',
    aliases: ['google_maps_url', 'maps_url', 'google_maps'],
  },
  {
    key: 'contact_name',
    label: 'Contact name (full)',
    aliases: ['contact_name', 'owner_name', 'contact', 'owner'],
  },
  {
    key: 'contact_first_name',
    label: 'Contact first name',
    aliases: [
      'contact_first_name',
      'first_name',
      'owner_first_name',
      'firstname',
    ],
  },
  {
    key: 'contact_last_name',
    label: 'Contact last name',
    aliases: [
      'contact_last_name',
      'last_name',
      'owner_last_name',
      'lastname',
      'surname',
    ],
  },
  {
    key: 'contact_email',
    label: 'Contact email',
    aliases: ['contact_email', 'email', 'email_address'],
  },
  {
    key: 'contact_title',
    label: 'Contact title',
    aliases: ['contact_title', 'title'],
  },
  {
    key: 'formatted_address',
    label: 'Address',
    aliases: ['address', 'formatted_address', 'full_address', 'street_address'],
  },
  { key: 'city', label: 'City', aliases: ['city', 'town'] },
  { key: 'state', label: 'State', aliases: ['state', 'province', 'region'] },
  {
    key: 'postal_code',
    label: 'Postal code',
    aliases: ['postal_code', 'zip', 'zipcode', 'zip_code'],
  },
  { key: 'market', label: 'Market', aliases: ['market'] },
  { key: 'cuisine', label: 'Cuisine', aliases: ['cuisine', 'category'] },
  {
    key: 'neighborhood',
    label: 'Neighborhood',
    aliases: ['neighborhood', 'neighbourhood', 'area'],
  },
  { key: 'timezone', label: 'Timezone', aliases: ['timezone', 'tz'] },
];

const TEMPLATE_CSV = [
  'name,restaurant_group_name,phone,website,contact_first_name,contact_last_name,contact_email,contact_title,address,city,state,zip,market,cuisine,neighborhood',
  'Rudy\'s Diner,Atlas Hospitality,+17185550100,https://rudysdiner.com,Stephen,Owner,owner@rudysdiner.com,Owner,"30-01 Broadway, Astoria, NY 11106",Astoria,NY,11106,NYC,Diner,Astoria',
].join('\n');

interface ParsedCsv {
  headers: string[];
  rows: Record<string, string>[];
  fileName: string;
}

interface ValidatedRow {
  index: number;
  payload: ProspectPayload;
  status: 'ok' | 'skipped_duplicate' | 'invalid';
  reason?: string;
}

interface ImportReport {
  created: Array<{ id: number; name: string }>;
  skipped_duplicates: Array<{
    row: number;
    field: string;
    value: string;
    existing_id: number | null;
    reason: string;
  }>;
  errors: Array<{ row: number; errors: Record<string, unknown> }>;
}

interface CsvImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImported: () => void;
}

const autoMapColumns = (headers: string[]): Record<string, string> => {
  const mapping: Record<string, string> = {};
  const normalizedHeaders = headers.map((h) => ({
    original: h,
    normalized: h
      .trim()
      .toLowerCase()
      .replace(/[\s-]+/g, '_'),
  }));
  for (const field of TARGET_FIELDS) {
    const match = normalizedHeaders.find((h) =>
      field.aliases.includes(h.normalized),
    );
    if (match) {
      mapping[field.key] = match.original;
    }
  }
  return mapping;
};

const CsvImportModal: React.FC<CsvImportModalProps> = ({
  isOpen,
  onClose,
  onImported,
}) => {
  const [step, setStep] = useState<Step>('upload');
  const [parsed, setParsed] = useState<ParsedCsv | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [report, setReport] = useState<ImportReport | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const reset = () => {
    setStep('upload');
    setParsed(null);
    setMapping({});
    setUploadError(null);
    setReport(null);
    setImportError(null);
    setSubmitting(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFile = (file: File | undefined) => {
    setUploadError(null);
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setUploadError('Please choose a .csv file.');
      return;
    }
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: 'greedy',
      complete: (result) => {
        const headers = (result.meta.fields ?? []).filter(Boolean);
        const rows = result.data;
        if (headers.length === 0 || rows.length === 0) {
          setUploadError('This file appears to be empty or has no header row.');
          return;
        }
        if (rows.length > MAX_ROWS) {
          setUploadError(
            `This file has ${rows.length.toLocaleString()} rows; the limit is ${MAX_ROWS.toLocaleString()} per import. Please split it into smaller files.`,
          );
          return;
        }
        setParsed({ headers, rows, fileName: file.name });
        setMapping(autoMapColumns(headers));
        setStep('map');
      },
      error: (err) =>
        setUploadError(`Could not parse the file: ${err.message}`),
    });
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'prospects_template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const sampleValue = (header: string): string => {
    if (!parsed) return '';
    for (const row of parsed.rows.slice(0, 5)) {
      const value = (row[header] ?? '').trim();
      if (value) return value;
    }
    return '';
  };

  const validatedRows: ValidatedRow[] = useMemo(() => {
    if (!parsed) return [];
    const seenEmails = new Set<string>();
    const seenPhones = new Set<string>();
    return parsed.rows.map((row, index) => {
      const payload: ProspectPayload = {
        name: '',
        source: 'csv',
        source_payload: row,
      };
      for (const field of TARGET_FIELDS) {
        const header = mapping[field.key];
        if (header) {
          const value = (row[header] ?? '').trim();
          if (value) {
            (payload as unknown as Record<string, unknown>)[field.key] = value;
          }
        }
      }
      if (!payload.name) {
        return {
          index,
          payload,
          status: 'invalid' as const,
          reason: 'Missing restaurant name',
        };
      }
      const email = normalizeEmail(payload.contact_email);
      if (email && !isValidEmail(email)) {
        return {
          index,
          payload,
          status: 'invalid' as const,
          reason: `Invalid email "${payload.contact_email}"`,
        };
      }
      const phone = normalizePhone(payload.phone);
      if (email && seenEmails.has(email)) {
        return {
          index,
          payload,
          status: 'skipped_duplicate' as const,
          reason: `Duplicate email in file (${email})`,
        };
      }
      if (phone && seenPhones.has(phone)) {
        return {
          index,
          payload,
          status: 'skipped_duplicate' as const,
          reason: `Duplicate phone in file (${payload.phone})`,
        };
      }
      if (email) seenEmails.add(email);
      if (phone) seenPhones.add(phone);
      return { index, payload, status: 'ok' as const };
    });
  }, [parsed, mapping]);

  const okRows = validatedRows.filter((r) => r.status === 'ok');
  const skippedRows = validatedRows.filter(
    (r) => r.status === 'skipped_duplicate',
  );
  const invalidRows = validatedRows.filter((r) => r.status === 'invalid');
  const nameIsMapped = Boolean(mapping['name']);

  const handleImport = async () => {
    setImportError(null);
    setSubmitting(true);
    try {
      const response = await api.post(apiEndpoints.importSalesProspects(), {
        prospects: okRows.map((r) => r.payload),
      });
      if (!response) {
        setImportError('Import failed: no response from server.');
        return;
      }
      const body = await response.json();
      if (!response.ok && response.status !== 200) {
        setImportError(
          `Import failed (status ${response.status}): ${JSON.stringify(body).substring(0, 200)}`,
        );
        return;
      }
      setReport(body as ImportReport);
      setStep('report');
      onImported();
    } catch (err) {
      setImportError(err instanceof Error ? err.message : 'Unexpected error.');
    } finally {
      setSubmitting(false);
    }
  };

  const stepIndex = ['upload', 'map', 'validate', 'report'].indexOf(step);

  const selectClass =
    'h-10 w-full appearance-none rounded-xl border border-gray-300 bg-transparent px-3 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-white/90';

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      className="max-w-3xl p-6 sm:p-8"
    >
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
        Import prospects from CSV
      </h2>

      <div className="mt-3 flex items-center gap-2">
        {['Upload', 'Map columns', 'Validate', 'Report'].map((label, i) => (
          <React.Fragment key={label}>
            {i > 0 && (
              <span
                className="h-px w-6 bg-gray-300 dark:bg-gray-700"
                aria-hidden
              />
            )}
            <span
              className={`rounded-full px-3 py-1 text-xs font-semibold ${
                i === stepIndex
                  ? 'bg-brand-500 text-white'
                  : i < stepIndex
                    ? 'bg-brand-100 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300'
                    : 'bg-gray-100 text-gray-500 dark:bg-white/[0.06] dark:text-gray-400'
              }`}
            >
              {i + 1}. {label}
            </span>
          </React.Fragment>
        ))}
      </div>

      <div className="mt-6">
        {step === 'upload' && (
          <div className="space-y-4">
            <label
              className="flex min-h-[160px] cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-gray-300 px-6 py-8 text-center transition-colors hover:border-brand-400 dark:border-gray-700"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                handleFile(e.dataTransfer.files?.[0]);
              }}
            >
              <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                Drop a CSV here, or click to choose a file
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Up to {MAX_ROWS.toLocaleString()} rows. Duplicates are matched
                on email and phone.
              </span>
              <input
                type="file"
                accept=".csv"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0] ?? undefined)}
              />
            </label>
            <button
              type="button"
              onClick={downloadTemplate}
              className="text-sm font-medium text-brand-600 underline hover:text-brand-700 dark:text-brand-400"
            >
              Download CSV template
            </button>
            {uploadError && (
              <div className="rounded-2xl border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-600 dark:border-error-800/70 dark:bg-error-900/20 dark:text-error-300">
                {uploadError}
              </div>
            )}
          </div>
        )}

        {step === 'map' && parsed && (
          <div className="space-y-4">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {parsed.fileName}: {parsed.rows.length.toLocaleString()} rows.
              Columns were auto-matched where possible — review and adjust
              below.
            </p>
            <div className="max-h-[45vh] overflow-y-auto rounded-2xl border border-gray-200 dark:border-gray-700">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-white/[0.03]">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                      Field
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                      CSV column
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                      Sample value
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                  {TARGET_FIELDS.map((field) => (
                    <tr key={field.key}>
                      <td className="px-4 py-2 font-medium text-gray-800 dark:text-gray-100">
                        {field.label}
                        {field.required && (
                          <span className="text-error-500"> *</span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <select
                          className={selectClass}
                          value={mapping[field.key] ?? ''}
                          onChange={(e) =>
                            setMapping((prev) => ({
                              ...prev,
                              [field.key]: e.target.value,
                            }))
                          }
                        >
                          <option value="">— not mapped —</option>
                          {parsed.headers.map((header) => (
                            <option key={header} value={header}>
                              {header}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="max-w-[200px] truncate px-4 py-2 text-gray-500 dark:text-gray-400">
                        {mapping[field.key]
                          ? sampleValue(mapping[field.key]) || '—'
                          : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {!nameIsMapped && (
              <div className="rounded-2xl border border-warning-300 bg-warning-50 px-4 py-3 text-sm text-warning-700 dark:border-warning-800/70 dark:bg-warning-900/20 dark:text-warning-300">
                Map a column to Restaurant name to continue.
              </div>
            )}
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={reset}
                className="app-button-secondary rounded-2xl px-4 py-2 text-sm font-medium"
              >
                Back
              </button>
              <button
                type="button"
                disabled={!nameIsMapped}
                onClick={() => setStep('validate')}
                className={`rounded-2xl bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 ${
                  nameIsMapped ? '' : 'cursor-not-allowed opacity-50'
                }`}
              >
                Continue to validation
              </button>
            </div>
          </div>
        )}

        {step === 'validate' && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-2xl border border-success-200 bg-success-50 px-4 py-3 text-center dark:border-success-800/60 dark:bg-success-900/15">
                <div className="text-2xl font-semibold text-success-700 dark:text-success-300">
                  {okRows.length}
                </div>
                <div className="text-xs text-success-700 dark:text-success-300">
                  Ready to import
                </div>
              </div>
              <div className="rounded-2xl border border-warning-300 bg-warning-50 px-4 py-3 text-center dark:border-warning-800/60 dark:bg-warning-900/15">
                <div className="text-2xl font-semibold text-warning-700 dark:text-warning-300">
                  {skippedRows.length}
                </div>
                <div className="text-xs text-warning-700 dark:text-warning-300">
                  In-file duplicates (skipped)
                </div>
              </div>
              <div className="rounded-2xl border border-error-200 bg-error-50 px-4 py-3 text-center dark:border-error-800/60 dark:bg-error-900/15">
                <div className="text-2xl font-semibold text-error-600 dark:text-error-300">
                  {invalidRows.length}
                </div>
                <div className="text-xs text-error-600 dark:text-error-300">
                  Invalid rows (skipped)
                </div>
              </div>
            </div>

            {(skippedRows.length > 0 || invalidRows.length > 0) && (
              <div className="max-h-[30vh] overflow-y-auto rounded-2xl border border-gray-200 dark:border-gray-700">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-white/[0.03]">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                        Row
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                        Name
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                        Issue
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                    {[...invalidRows, ...skippedRows].map((row) => (
                      <tr key={row.index}>
                        <td className="px-4 py-2 text-gray-500">
                          {row.index + 2}
                        </td>
                        <td className="px-4 py-2 text-gray-800 dark:text-gray-100">
                          {row.payload.name || '—'}
                        </td>
                        <td className="px-4 py-2 text-gray-500 dark:text-gray-400">
                          {row.reason}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <p className="text-xs text-gray-500 dark:text-gray-400">
              Rows already in the database (matched on email or phone) will be
              skipped by the server and reported in the next step.
            </p>

            {importError && (
              <div className="rounded-2xl border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-600 dark:border-error-800/70 dark:bg-error-900/20 dark:text-error-300">
                {importError}
              </div>
            )}

            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setStep('map')}
                className="app-button-secondary rounded-2xl px-4 py-2 text-sm font-medium"
              >
                Back
              </button>
              <button
                type="button"
                disabled={okRows.length === 0 || submitting}
                onClick={handleImport}
                className={`rounded-2xl bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 ${
                  okRows.length === 0 || submitting
                    ? 'cursor-not-allowed opacity-50'
                    : ''
                }`}
              >
                {submitting
                  ? 'Importing…'
                  : `Import ${okRows.length.toLocaleString()} prospects`}
              </button>
            </div>
          </div>
        )}

        {step === 'report' && report && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-2xl border border-success-200 bg-success-50 px-4 py-3 text-center dark:border-success-800/60 dark:bg-success-900/15">
                <div className="text-2xl font-semibold text-success-700 dark:text-success-300">
                  {report.created.length}
                </div>
                <div className="text-xs text-success-700 dark:text-success-300">
                  Created
                </div>
              </div>
              <div className="rounded-2xl border border-warning-300 bg-warning-50 px-4 py-3 text-center dark:border-warning-800/60 dark:bg-warning-900/15">
                <div className="text-2xl font-semibold text-warning-700 dark:text-warning-300">
                  {report.skipped_duplicates.length}
                </div>
                <div className="text-xs text-warning-700 dark:text-warning-300">
                  Skipped duplicates
                </div>
              </div>
              <div className="rounded-2xl border border-error-200 bg-error-50 px-4 py-3 text-center dark:border-error-800/60 dark:bg-error-900/15">
                <div className="text-2xl font-semibold text-error-600 dark:text-error-300">
                  {report.errors.length}
                </div>
                <div className="text-xs text-error-600 dark:text-error-300">
                  Errors
                </div>
              </div>
            </div>

            {(report.skipped_duplicates.length > 0 ||
              report.errors.length > 0) && (
              <div className="max-h-[30vh] overflow-y-auto rounded-2xl border border-gray-200 dark:border-gray-700">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-white/[0.03]">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                        Row
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-semibold uppercase text-gray-500">
                        Issue
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                    {report.skipped_duplicates.map((item) => (
                      <tr key={`dup-${item.row}`}>
                        <td className="px-4 py-2 text-gray-500">
                          {item.row + 1}
                        </td>
                        <td className="px-4 py-2 text-gray-500 dark:text-gray-400">
                          Duplicate {item.field === 'phone' ? 'phone' : 'email'}{' '}
                          ({item.value})
                          {item.existing_id
                            ? ` — matches prospect #${item.existing_id}`
                            : ''}
                        </td>
                      </tr>
                    ))}
                    {report.errors.map((item) => (
                      <tr key={`err-${item.row}`}>
                        <td className="px-4 py-2 text-gray-500">
                          {item.row + 1}
                        </td>
                        <td className="px-4 py-2 text-error-600 dark:text-error-300">
                          {JSON.stringify(item.errors)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleClose}
                className="rounded-2xl bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default CsvImportModal;
